import { useContext, useState, useEffect, useRef } from "react";
import { ChartContext } from "../../../Context/ChartContext/ChartContext";
import MainContext from "../../../Context/MainContext/MainContext";

import MUIButton from "@mui/material/Button";
import MUIGrid from "@mui/material/Grid2";
import MUIBox from "@mui/material/Box";
import MUITextField from "@mui/material/TextField";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUITypography from "@mui/material/Typography";
import MUIStack from "@mui/material/Stack";
import MUIFormControlLabel from "@mui/material/FormControlLabel";
import MUISwitch from "@mui/material/Switch";
import MUICheckbox from "@mui/material/Checkbox";
import MUIPersonIcon from "@mui/icons-material/Person";
import MUIContentPasteIcon from "@mui/icons-material/ContentPaste";

import Provider from "../../../Utility/Crud/Provider";
import DiagnosisCode from "../../../Utility/Crud/DiagnosisCode";
import Card from "../../Card";
import Throttle from "../../../Utility/Throttle";
import InfoItem from "../../InfoItem";
import Format from "../../../Utility/Format";
import api from "../../../Seating/Security/api";
import Validator from "../../../Utility/Validator";
import Security from "../../../Utility/Security";

/**
 * CardChartClinical renders a Card component
 * displaying patient and orderActivity clinical information.
 * @returns {JSX.Element}
 */

const title = "Clinical";
const icon = MUIContentPasteIcon;

const className = "CardChartClinical";

export default function CardChartClinical(props) {
  const chartContext = useContext(ChartContext);
  const patient = chartContext.chart.patient[chartContext.patientId];
  const originalPatient =
    chartContext.originalChart.patient[chartContext.patientId];
  const orderActivity = chartContext.chart.orderActivity[chartContext.orderId];
  const target = orderActivity ? "orderActivity" : "patient";
  const targetId =
    target === "orderActivity" ? chartContext.orderId : chartContext.patientId;
  const orderingProviderId =
    chartContext.chart[target][targetId]?.orderingProviderId;
  const provider = chartContext.chart.provider[orderingProviderId];
  const jsonOrderingProviderNotFoundData = JSON.parse(
    chartContext.chart[target][targetId]?.jsonOrderingProviderNotFoundData ??
      "{}",
  );
  const providerNotFound =
    chartContext.chart[target][targetId].orderingProviderNotFound;
  const [editMode, setEditMode] = useState(false);
  const [cardChart, setCardChart] = useState(null);
  const [copyToPatient, setCopyToPatient] = useState(false);
  const [providersData, setProvidersData] = useState([]);
  const [diagnosisCodesFound, setDiagnosisCodesFound] = useState([]);
  const [originalDiagnosisCodes, setOriginalDiagnosisCodes] = useState([]);
  const [providerInput, setProviderInput] = useState("");
  const pendingProviderEditOriginals = useRef({});
  const [diagnosisCodesInputs, setDiagnosisCodesInputs] = useState({});
  const selectedProvider =
    providersData?.find(
      (providerRow) => +providerRow?.id === +orderingProviderId,
    ) || null;
  /**
   * Gets a provider object from either the chart data or JSON blob.
   */
  useEffect(() => {
    if (provider) {
      setProvidersData([provider]);
      setProviderInput(Provider.getDetailedDisplayName(provider) || "");
    } else {
      setProvidersData([]);
    }

    // Only include diagnosisCodes that are referenced in chartContext.chart[target][targetId]
    const diagnosisCodeKeys = getDiagnosisCodeKeys(
      chartContext.chart[target][targetId],
    );
    const foundCodes = diagnosisCodeKeys
      .map((key) => chartContext.chart[target][targetId]?.[key])
      .map((codeId) => (chartContext.chart.diagnosisCode || {})[codeId]);
    setOriginalDiagnosisCodes(foundCodes);
    setDiagnosisCodesFound(foundCodes);

    // Set diagnosisCodesInputs to the display names of those codes
    const diagnosisCodesInputs = Object.fromEntries(
      diagnosisCodeKeys.map((key, idx) => [
        key,
        foundCodes[idx]
          ? DiagnosisCode.getDetailedDisplayName(foundCodes[idx]) || ""
          : "",
      ]),
    );
    setDiagnosisCodesInputs(diagnosisCodesInputs);
  }, []);

  /**
   * Sets up validation rules when in edit mode for ordering provider not found.
   */
  useEffect(() => {
    const rules = [];

    if (
      editMode &&
      chartContext.chart[target][targetId]?.orderingProviderNotFound
    ) {
      rules.push({
        target: target,
        id: targetId,
        field: "jsonOrderingProviderNotFoundData",
        rule: {
          type: "custom",
          function: (entity) => {
            const data = JSON.parse(entity || "{}");
            const errors = {};
            if (!data.name) {
              errors.name = "Name is required.";
            }
            if (data.npi && !Validator.validateNPI(data.npi)) {
              errors.npi = "NPI must be 10 digits.";
            }
            if (
              data.postal_code &&
              !Validator.validatePostalCode(data.postal_code)
            ) {
              errors.postal_code = "Postal Code must be 5 digits or ZIP+4.";
            }
            if (
              data.phone_number &&
              !Validator.validatePhone(data.phone_number)
            ) {
              errors.phone_number = "Phone Number must be 10 digits.";
            }
            if (data.fax_number && !Validator.validatePhone(data.fax_number)) {
              errors.fax_number = "Fax Number must be 10 digits.";
            }
            return Object.keys(errors).length > 0 ? errors : true;
          },
        },
      });
    }

    if (
      editMode &&
      !chartContext.chart[target][targetId]?.orderingProviderNotFound &&
      Security.hasPermission("provider.edit") &&
      selectedProvider?.id
    ) {
      rules.push(
        {
          target: target,
          id: targetId,
          field: "providerPostalCodeValidation",
          rule: {
            type: "custom",
            function: () => {
              const value = selectedProvider?.postalCode;
              if (!value || Validator.validatePostalCode(value)) {
                return true;
              }
              return "Postal Code must be 5 digits or ZIP+4.";
            },
          },
        },
        {
          target: target,
          id: targetId,
          field: "providerPhoneNumberValidation",
          rule: {
            type: "custom",
            function: () => {
              const value = selectedProvider?.phoneNumber;
              if (!value || Validator.validatePhone(value)) {
                return true;
              }
              return "Phone Number must be 10 digits.";
            },
          },
        },
        {
          target: target,
          id: targetId,
          field: "providerFaxNumberValidation",
          rule: {
            type: "custom",
            function: () => {
              const value = selectedProvider?.faxNumber;
              if (!value || Validator.validatePhone(value)) {
                return true;
              }
              return "Fax Number must be 10 digits.";
            },
          },
        },
      );
    }

    if (rules.length > 0) {
      chartContext.updateValidationRules({ key: className, rules: rules });
    } else {
      chartContext.updateValidationRules({ key: className, rules: [] });
    }
  }, [
    editMode,
    chartContext.chart[target][targetId]?.orderingProviderNotFound,
    copyToPatient,
    selectedProvider?.id,
    selectedProvider?.postalCode,
    selectedProvider?.phoneNumber,
    selectedProvider?.faxNumber,
    chartContext.chart[target][targetId]?.orderingProviderId,
  ]);

  // Close edit mode if originalChart changes (on save)
  useEffect(() => {
    setEditMode(false);
    setCopyToPatient(false);
    pendingProviderEditOriginals.current = {};
  }, [chartContext.originalChart]);

  // Sync this card's edit mode with ChartContext for ChartNavigationBar display
  useEffect(() => {
    chartContext.setCardEditMode?.(className, editMode);
    return () => chartContext.setCardEditMode?.(className, false);
  }, [editMode, chartContext.setCardEditMode]);

  /**
   * Retrieves diagnosis codes associated with the given target.
   * @returns {Array} An array of JSX elements representing the diagnosis codes.
   */
  const getReadonlyDiagnosisCodes = () => {
    if (!patient) {
      return [];
    }
    if (!orderActivity && target === "orderActivity") {
      return [];
    }
    const diagnosisCodeKeys = getDiagnosisCodeKeys(
      chartContext.chart[target][targetId],
    );

    let diagnosisCodes = diagnosisCodeKeys.map((key) => {
      const diagnosisCode =
        chartContext.chart.diagnosisCode[
          chartContext.chart[target][targetId]?.[key]
        ];
      return diagnosisCode ? (
        <span key={key}>
          {DiagnosisCode.getDetailedDisplayName(diagnosisCode)} <br></br>
        </span>
      ) : null;
    });
    return diagnosisCodes.every((item) => item === null)
      ? ["None"]
      : diagnosisCodes;
  };

  /**
   * Handles changes to the account search input.
   * Sends a POST request to fetch accounts matching the search term.
   * Updates the accountsFound state with the results.
   * @param {string} providerName - The name of the provider being searched.
   */
  const handleProviderChange = (providerName) => {
    api
      .send(
        "POST",
        `/search/omniSearch/Provider`,
        { omniSearchTerm: providerName, recordLimit: 20 },
        2,
        true,
      )
      .then((data) => {
        setProvidersData(data.providers || []);
      })
      .catch((error) => {
        console.error("Error fetching providers:", error);
      });
  };

  /**
   * Handles changes to the diagnosis code search input.
   * Sends a POST request to fetch diagnosis codes matching the search term.
   * Updates the diagnosisCodesFound state with the results.
   * @param {string} code - The diagnosis code search term.
   */
  const handleDiagnosisCodeChange = (code) => {
    api
      .send(
        "POST",
        `/search/omniSearch/DiagnosisCode`,
        { omniSearchTerm: code, recordLimit: 20 },
        2,
        true,
      )
      .then((data) => {
        setDiagnosisCodesFound(data.diagnosisCodes || []);
      })
      .catch((error) => {
        console.error("Error fetching diagnosis codes:", error);
      });
  };

  /**
   * Throttled function to handle account search input changes.
   * Limits the frequency of API calls to once every 100 milliseconds.
   * @param {string} provider - The NPI or Name of the provider being searched.
   */
  const throttledHandleProviderSearch = Throttle.throttle(
    (provider) => {
      handleProviderChange(provider);
    },
    100,
    { trailing: true },
  );

  /**
   * Throttled function to handle diagnosis code search input changes.
   * Limits the frequency of API calls to once every 100 milliseconds.
   * @param {string} code - The diagnosis code or search term being searched.
   */
  const throttledHandleDiagnosisCodeSearch = Throttle.throttle(
    (code) => {
      handleDiagnosisCodeChange(code);
    },
    100,
    { trailing: true },
  );

  /**
   * Returns the styles for a container that should be full width at a given max width.
   * Only used when sidebar=true to handle responsive container queries.
   * @param {number} maxWidth - The max width at which the container should be full width.
   * @returns {Object} The styles for the container.
   */
  const getContainerQuery = (maxWidth) => {
    if (!props.sidebar) {
      return {};
    }

    return {
      [`@container (max-width: ${maxWidth}px)`]: {
        flexBasis: "100% !important",
        maxWidth: "100% !important",
      },
    };
  };

  function updateChartField(fields) {
    chartContext.onChange({
      [target]: {
        [targetId]: { ...chartContext.chart[target][targetId], ...fields },
      },
    });
    setCardChart({ ...cardChart, ...fields });
  }

  function rollbackProviderContactEdits(providerId = null) {
    const ids = providerId
      ? [providerId]
      : Object.keys(pendingProviderEditOriginals.current);
    ids.forEach((id) => {
      const originalProvider = pendingProviderEditOriginals.current[id];
      if (!originalProvider) {
        return;
      }

      chartContext.onChange({ provider: { [id]: originalProvider } });
      setProvidersData((current) => {
        const withoutSelected = current.filter((p) => +p?.id !== +id);
        return [originalProvider, ...withoutSelected];
      });
      delete pendingProviderEditOriginals.current[id];
    });
  }

  /**
   * Returns the error value for a given field from chartContext.validationIssues.
   * @param {string} field - The field name to check for errors.
   * @param {boolean} disabled - Whether the field is disabled.
   * @returns {boolean} True if there is an error, otherwise false.
   */
  function getFieldHelpers(field, disabled = false, isHelper) {
    if (disabled) {
      return false;
    }
    if (
      !target ||
      !targetId ||
      !chartContext.validationIssues[className]?.[target]
    ) {
      return false;
    }
    if (isHelper) {
      return (
        chartContext.validationIssues[className][target][targetId]
          ?.jsonOrderingProviderNotFoundData?.[field] || ""
      );
    } else {
      return !!chartContext.validationIssues[className][target][targetId]
        ?.jsonOrderingProviderNotFoundData?.[field];
    }
  }

  function getProviderFieldHelpers(field, isHelper = false) {
    if (!selectedProvider?.id) {
      return isHelper ? "" : false;
    }
    const providerValidationFieldMap = {
      postalCode: "providerPostalCodeValidation",
      phoneNumber: "providerPhoneNumberValidation",
      faxNumber: "providerFaxNumberValidation",
    };
    const validationField = providerValidationFieldMap[field];
    const message =
      chartContext.validationIssues[className]?.[target]?.[targetId]?.[
        validationField
      ] || "";
    return isHelper ? message : !!message;
  }

  const stateListOptions = [
    { value: "Alabama", label: "Alabama" },
    { value: "Alaska", label: "Alaska" },
    { value: "Arizona", label: "Arizona" },
    { value: "Arkansas", label: "Arkansas" },
    { value: "California", label: "California" },
    { value: "Colorado", label: "Colorado" },
    { value: "Connecticut", label: "Connecticut" },
    { value: "Delaware", label: "Delaware" },
    { value: "Florida", label: "Florida" },
    { value: "Georgia", label: "Georgia" },
    { value: "Hawaii", label: "Hawaii" },
    { value: "Idaho", label: "Idaho" },
    { value: "Illinois", label: "Illinois" },
    { value: "Indiana", label: "Indiana" },
    { value: "Iowa", label: "Iowa" },
    { value: "Kansas", label: "Kansas" },
    { value: "Kentucky", label: "Kentucky" },
    { value: "Louisiana", label: "Louisiana" },
    { value: "Maine", label: "Maine" },
    { value: "Maryland", label: "Maryland" },
    { value: "Massachusetts", label: "Massachusetts" },
    { value: "Michigan", label: "Michigan" },
    { value: "Minnesota", label: "Minnesota" },
    { value: "Mississippi", label: "Mississippi" },
    { value: "Missouri", label: "Missouri" },
    { value: "Montana", label: "Montana" },
    { value: "Nebraska", label: "Nebraska" },
    { value: "Nevada", label: "Nevada" },
    { value: "New Hampshire", label: "New Hampshire" },
    { value: "New Jersey", label: "New Jersey" },
    { value: "New Mexico", label: "New Mexico" },
    { value: "New York", label: "New York" },
    { value: "North Carolina", label: "North Carolina" },
    { value: "North Dakota", label: "North Dakota" },
    { value: "Ohio", label: "Ohio" },
    { value: "Oklahoma", label: "Oklahoma" },
    { value: "Oregon", label: "Oregon" },
    { value: "Pennsylvania", label: "Pennsylvania" },
    { value: "Rhode Island", label: "Rhode Island" },
    { value: "South Carolina", label: "South Carolina" },
    { value: "South Dakota", label: "South Dakota" },
    { value: "Tennessee", label: "Tennessee" },
    { value: "Texas", label: "Texas" },
    { value: "Utah", label: "Utah" },
    { value: "Vermont", label: "Vermont" },
    { value: "Virginia", label: "Virginia" },
    { value: "Washington", label: "Washington" },
    { value: "West Virginia", label: "West Virginia" },
    { value: "Wisconsin", label: "Wisconsin" },
    { value: "Wyoming", label: "Wyoming" },
    { value: "District of Columbia", label: "District of Columbia" },
  ];

  useEffect(() => {
    if (copyToPatient) {
      copyToPatientFunction();
    }
  }, [
    copyToPatient,
    chartContext.chart[target][targetId]?.jsonOrderingProviderNotFoundData,
    chartContext.chart[target][targetId]?.orderingProviderId,
    chartContext.chart[target][targetId]?.orderingProviderNotFound,
  ]);

  function copyToPatientFunction() {
    if (orderActivity.orderingProviderId === null && providerNotFound) {
      chartContext.onChange({
        patient: {
          [chartContext.patientId]: {
            jsonOrderingProviderNotFoundData: JSON.stringify(
              jsonOrderingProviderNotFoundData,
            ),
            orderingProviderId: null,
            orderingProviderNotFound: true,
          },
        },
      });
      setCardChart({
        ...cardChart,
        jsonOrderingProviderNotFoundData: JSON.stringify(
          jsonOrderingProviderNotFoundData,
        ),
        orderingProviderId: null,
        orderingProviderNotFound: true,
      });
    } else if (orderActivity.orderingProviderId !== null) {
      chartContext.onChange({
        patient: {
          [chartContext.patientId]: {
            orderingProviderId: orderActivity.orderingProviderId,
            orderingProviderNotFound: false,
          },
        },
      });
      setCardChart({
        ...cardChart,
        orderingProviderId: orderActivity.orderingProviderId,
        orderingProviderNotFound: false,
      });
    } else {
      chartContext.onChange({
        patient: {
          [chartContext.patientId]: {
            jsonOrderingProviderNotFoundData: JSON.stringify(
              jsonOrderingProviderNotFoundData,
            ),
            orderingProviderId: null,
            orderingProviderNotFound: null,
          },
        },
      });
    }
  }

  /**
   * Renders the ordering provider section.
   * @returns {JSX.Element} Grid layout with ordering provider information display
   */
  const renderOrderingProviderSection = () => {
    return (
      <MUIGrid container spacing={2}>
        <MUIGrid size={12}>
          <MUIStack
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <MUIStack direction="row" spacing={1}>
              <MUIPersonIcon
                fontSize="medium"
                sx={{ color: (theme) => theme.palette.secondary.main }}
              />
              <MUITypography variant="subtitle2">
                Ordering Provider
              </MUITypography>
            </MUIStack>
            <MUIFormControlLabel
              sx={{ mr: 0 }}
              control={
                <MUICheckbox
                  size="small"
                  checked={
                    chartContext.chart[target][targetId]
                      ?.orderingProviderNotFound || false
                  }
                  onChange={(event, value) => {
                    rollbackProviderContactEdits(orderingProviderId);
                    if (value) {
                      updateChartField({
                        orderingProviderId: null,
                        orderingProviderNotFound: value,
                      });
                    } else {
                      updateChartField({
                        orderingProviderNotFound: value,
                        jsonOrderingProviderNotFoundData:
                          chartContext.originalChart[target][targetId]
                            .jsonOrderingProviderNotFoundData,
                      });
                    }
                    setProviderInput("");
                    clearValidationIssues();
                  }}
                />
              }
              label="Not Found"
            />
          </MUIStack>
        </MUIGrid>
        <MUIGrid sx={getContainerQuery(600)} size={12}>
          <MUIStack
            direction="row"
            spacing={0.5}
            sx={{
              alignItems: "flex-end",
              width: "100%",
              flexWrap: "nowrap",
            }}
          >
            <MUIBox sx={{ flex: 1, minWidth: 0 }}>
              <MUIAutocomplete
                options={
                  providersData.length === 1 &&
                  +providersData[0]?.id === +orderingProviderId
                    ? []
                    : providersData
                }
                value={selectedProvider}
                isOptionEqualToValue={(option, value) => {
                  const result = +option?.id === +value?.id;
                  return result;
                }}
                inputValue={providerInput}
                getOptionKey={(option) => option?.id || ""}
                getOptionLabel={(option) =>
                  Provider.getDetailedDisplayName(option) || ""
                }
                disabled={
                  chartContext.chart[target][targetId]?.orderingProviderNotFound
                }
                onChange={(e, newValue) => {
                  rollbackProviderContactEdits(orderingProviderId);
                  updateChartField({
                    orderingProviderId: newValue?.id || null,
                  });
                  setProviderInput(
                    (newValue && Provider.getDetailedDisplayName(newValue)) ||
                      "",
                  );
                }}
                onInputChange={(e, newInputValue, reason) => {
                  if (reason == "input") {
                    rollbackProviderContactEdits(orderingProviderId);
                    updateChartField({ orderingProviderId: null });
                    setProviderInput(newInputValue);
                    throttledHandleProviderSearch(newInputValue);
                  }
                }}
                noOptionsText="Type to search..."
                onBlur={() => {
                  setProviderInput(
                    selectedProvider
                      ? Provider.getDetailedDisplayName(selectedProvider) || ""
                      : "",
                  );
                }}
                renderInput={(params) => (
                  <MUITextField
                    {...params}
                    size="small"
                    variant="standard"
                    label="Ordering Provider"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </MUIBox>
          </MUIStack>
        </MUIGrid>
        {target === "orderActivity" && (
          <MUIGrid
            sx={{
              margin: 1,
              padding: 1,
              borderRadius: 1,
              backgroundColor: (theme) => theme.palette.background.default,
              ...getContainerQuery(600),
            }}
            size={{
              lg: 12,
              md: 12,
              xs: 12,
            }}
          >
            <MUIFormControlLabel
              control={
                <MUISwitch
                  checked={copyToPatient}
                  onChange={(event, value) => {
                    if (!value) {
                      chartContext.onChange({
                        patient: {
                          [chartContext.patientId]: {
                            jsonOrderingProviderNotFoundData:
                              originalPatient.jsonOrderingProviderNotFoundData,
                            orderingProviderId:
                              originalPatient.orderingProviderId,
                            orderingProviderNotFound:
                              originalPatient.orderingProviderNotFound,
                          },
                        },
                      });
                      setCardChart({
                        ...cardChart,
                        jsonOrderingProviderNotFoundData:
                          originalPatient.jsonOrderingProviderNotFoundData,
                        orderingProviderId: originalPatient.orderingProviderId,
                        orderingProviderNotFound:
                          originalPatient.orderingProviderNotFound,
                      });
                    } else {
                      chartContext.onChange({
                        patient: {
                          [chartContext.patientId]: {
                            jsonOrderingProviderNotFoundData: JSON.stringify(
                              jsonOrderingProviderNotFoundData,
                            ),
                            orderingProviderId: null,
                            orderingProviderNotFound: true,
                          },
                        },
                      });
                      setCardChart({
                        ...cardChart,
                        jsonOrderingProviderNotFoundData: JSON.stringify(
                          jsonOrderingProviderNotFoundData,
                        ),
                        orderingProviderId: null,
                        orderingProviderNotFound: true,
                      });
                    }
                    setCopyToPatient(value);
                  }}
                />
              }
              label="Copy to Patient"
            />
          </MUIGrid>
        )}
      </MUIGrid>
    );
  };

  /**
   * Renders the ordering provider not found section of the chart.
   * @returns {JSX.Element} Grid layout with ordering provider not found information display
   */
  const renderProviderNotFoundSection = () => {
    const disabled =
      !chartContext.chart[target][targetId]?.orderingProviderNotFound;
    const useProviderContactEditing =
      disabled && Security.hasPermission("provider.edit") && !!selectedProvider;
    const updateSelectedProvider = (fields) => {
      if (!selectedProvider?.id) {
        return;
      }

      if (!pendingProviderEditOriginals.current[selectedProvider.id]) {
        pendingProviderEditOriginals.current[selectedProvider.id] = {
          ...(chartContext.originalChart.provider?.[selectedProvider.id] ||
            selectedProvider),
        };
      }

      const updatedProvider = { ...selectedProvider, ...fields };
      chartContext.onChange({
        provider: { [selectedProvider.id]: updatedProvider },
      });
      setProvidersData((current) => {
        const withoutSelected = current.filter(
          (p) => +p?.id !== +selectedProvider.id,
        );
        return [updatedProvider, ...withoutSelected];
      });
      setCardChart({ ...cardChart, orderingProviderId: selectedProvider.id });
    };

    return (
      <MUIGrid container spacing={2}>
        <MUIGrid
          sx={getContainerQuery(600)}
          size={{
            lg: 12,
            md: 6,
            xs: 12,
          }}
        >
          <MUITextField
            value={
              disabled
                ? Provider.getDisplayName(selectedProvider) || ""
                : jsonOrderingProviderNotFoundData.name || ""
            }
            size="small"
            label="Name"
            variant="standard"
            disabled={disabled || useProviderContactEditing}
            required={
              chartContext.chart[target][targetId]?.orderingProviderNotFound
            }
            fullWidth={true}
            error={getFieldHelpers("name", disabled)}
            helperText={getFieldHelpers("name", disabled, true)}
            onChange={(e) => {
              const updated = {
                ...jsonOrderingProviderNotFoundData,
                name: e.target.value,
              };
              updateChartField({
                jsonOrderingProviderNotFoundData: JSON.stringify(updated),
              });
            }}
            slotProps={{
              htmlInput: {
                maxLength: 255,
              },

              inputLabel: { shrink: true },
            }}
          />
        </MUIGrid>
        <MUIGrid
          sx={getContainerQuery(600)}
          size={{
            lg: 12,
            md: 6,
            xs: 12,
          }}
        >
          <MUITextField
            value={
              disabled
                ? selectedProvider?.npi || ""
                : jsonOrderingProviderNotFoundData.npi || ""
            }
            size="small"
            label="NPI"
            variant="standard"
            disabled={disabled || useProviderContactEditing}
            fullWidth={true}
            error={getFieldHelpers("npi", disabled)}
            helperText={getFieldHelpers("npi", disabled, true)}
            onChange={(e) => {
              const cleaned = e.target.value.replace(/\D/g, "").slice(0, 10);
              const updated = {
                ...jsonOrderingProviderNotFoundData,
                npi: cleaned,
              };

              updateChartField({
                jsonOrderingProviderNotFoundData: JSON.stringify(updated),
              });
            }}
            slotProps={{
              htmlInput: {
                maxLength: 10,
              },

              inputLabel: { shrink: true },
            }}
          />
        </MUIGrid>
        <MUIGrid
          sx={getContainerQuery(600)}
          size={{
            lg: 12,
            md: 6,
            xs: 12,
          }}
        >
          <MUITextField
            value={
              disabled
                ? selectedProvider?.address1 || ""
                : jsonOrderingProviderNotFoundData.address || ""
            }
            size="small"
            label="Address"
            variant="standard"
            disabled={
              !chartContext.chart[target][targetId]?.orderingProviderNotFound &&
              !useProviderContactEditing
            }
            fullWidth={true}
            onChange={(e) => {
              if (useProviderContactEditing) {
                updateSelectedProvider({ address1: e.target.value });
              } else {
                const updated = {
                  ...jsonOrderingProviderNotFoundData,
                  address: e.target.value,
                };
                updateChartField({
                  jsonOrderingProviderNotFoundData: JSON.stringify(updated),
                });
              }
            }}
            slotProps={{
              htmlInput: {
                maxLength: 255,
              },

              inputLabel: { shrink: true },
            }}
          />
        </MUIGrid>
        <MUIGrid
          sx={getContainerQuery(600)}
          size={{
            lg: 5,
            md: 6,
            xs: 12,
          }}
        >
          <MUITextField
            value={
              disabled
                ? selectedProvider?.city || ""
                : jsonOrderingProviderNotFoundData.city || ""
            }
            size="small"
            label="City"
            variant="standard"
            disabled={
              !chartContext.chart[target][targetId]?.orderingProviderNotFound &&
              !useProviderContactEditing
            }
            fullWidth={true}
            onChange={(e) => {
              if (useProviderContactEditing) {
                updateSelectedProvider({ city: e.target.value });
              } else {
                const updated = {
                  ...jsonOrderingProviderNotFoundData,
                  city: e.target.value,
                };
                updateChartField({
                  jsonOrderingProviderNotFoundData: JSON.stringify(updated),
                });
              }
            }}
            slotProps={{
              htmlInput: {
                maxLength: 255,
              },

              inputLabel: { shrink: true },
            }}
          />
        </MUIGrid>
        <MUIGrid
          sx={getContainerQuery(600)}
          size={{
            lg: 4,
            md: 6,
            xs: 12,
          }}
        >
          <MUIAutocomplete
            options={stateListOptions}
            value={
              stateListOptions.find(
                (option) =>
                  option.value ===
                  (disabled
                    ? selectedProvider?.state
                    : jsonOrderingProviderNotFoundData.state),
              ) || null
            }
            isOptionEqualToValue={(option, value) =>
              option?.value === value?.value
            }
            getOptionLabel={(option) => option?.label || ""}
            onChange={(e, newValue) => {
              if (useProviderContactEditing) {
                updateSelectedProvider({
                  state: newValue ? newValue.value : "",
                });
              } else {
                const updated = {
                  ...jsonOrderingProviderNotFoundData,
                  state: newValue ? newValue.value : "",
                };
                updateChartField({
                  jsonOrderingProviderNotFoundData: JSON.stringify(updated),
                });
              }
            }}
            disabled={
              !chartContext.chart[target][targetId]?.orderingProviderNotFound &&
              !useProviderContactEditing
            }
            renderInput={(params) => (
              <MUITextField
                {...params}
                size="small"
                variant="standard"
                label="State"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </MUIGrid>
        <MUIGrid
          sx={getContainerQuery(600)}
          size={{
            lg: 3,
            md: 6,
            xs: 12,
          }}
        >
          <MUITextField
            value={
              disabled
                ? selectedProvider?.postalCode || ""
                : jsonOrderingProviderNotFoundData.postal_code || ""
            }
            size="small"
            label="Postal Code"
            variant="standard"
            disabled={
              !chartContext.chart[target][targetId]?.orderingProviderNotFound &&
              !useProviderContactEditing
            }
            fullWidth={true}
            error={
              useProviderContactEditing
                ? getProviderFieldHelpers("postalCode")
                : getFieldHelpers("postal_code", disabled)
            }
            helperText={
              useProviderContactEditing
                ? getProviderFieldHelpers("postalCode", true)
                : getFieldHelpers("postal_code", disabled, true)
            }
            onChange={(e) => {
              const cleaned = e.target.value
                .replace(/[^\d-]/g, "")
                .slice(0, 10);
              if (useProviderContactEditing) {
                updateSelectedProvider({ postalCode: cleaned });
              } else {
                const updated = {
                  ...jsonOrderingProviderNotFoundData,
                  postal_code: cleaned,
                };
                updateChartField({
                  jsonOrderingProviderNotFoundData: JSON.stringify(updated),
                });
              }
            }}
            slotProps={{
              htmlInput: {
                maxLength: 10,
              },

              inputLabel: { shrink: true },
            }}
          />
        </MUIGrid>
        <MUIGrid
          sx={getContainerQuery(600)}
          size={{
            lg: 6,
            md: 6,
            xs: 12,
          }}
        >
          <MUITextField
            value={
              disabled
                ? Format.phone(selectedProvider?.phoneNumber) || ""
                : Format.phone(jsonOrderingProviderNotFoundData.phone_number) ||
                  ""
            }
            size="small"
            label="Phone"
            variant="standard"
            disabled={
              !chartContext.chart[target][targetId]?.orderingProviderNotFound &&
              !useProviderContactEditing
            }
            fullWidth={true}
            error={
              useProviderContactEditing
                ? getProviderFieldHelpers("phoneNumber")
                : getFieldHelpers("phone_number", disabled)
            }
            helperText={
              useProviderContactEditing
                ? getProviderFieldHelpers("phoneNumber", true)
                : getFieldHelpers("phone_number", disabled, true)
            }
            onChange={(e) => {
              const cleaned = e.target.value.replace(/\D/g, "").slice(0, 10);
              if (useProviderContactEditing) {
                updateSelectedProvider({ phoneNumber: cleaned });
              } else {
                const updated = {
                  ...jsonOrderingProviderNotFoundData,
                  phone_number: cleaned,
                };
                updateChartField({
                  jsonOrderingProviderNotFoundData: JSON.stringify(updated),
                });
              }
            }}
            slotProps={{
              htmlInput: {
                maxLength: 14,
              },

              inputLabel: { shrink: true },
            }}
          />
        </MUIGrid>
        <MUIGrid
          sx={getContainerQuery(600)}
          size={{
            lg: 6,
            md: 6,
            xs: 12,
          }}
        >
          <MUITextField
            value={
              disabled
                ? Format.phone(selectedProvider?.faxNumber) || ""
                : Format.phone(jsonOrderingProviderNotFoundData.fax_number) ||
                  ""
            }
            size="small"
            label="Fax"
            variant="standard"
            disabled={
              !chartContext.chart[target][targetId]?.orderingProviderNotFound &&
              !useProviderContactEditing
            }
            fullWidth={true}
            error={
              useProviderContactEditing
                ? getProviderFieldHelpers("faxNumber")
                : getFieldHelpers("fax_number", disabled)
            }
            helperText={
              useProviderContactEditing
                ? getProviderFieldHelpers("faxNumber", true)
                : getFieldHelpers("fax_number", disabled, true)
            }
            onChange={(e) => {
              const cleaned = e.target.value.replace(/\D/g, "").slice(0, 10);
              if (useProviderContactEditing) {
                updateSelectedProvider({ faxNumber: cleaned });
              } else {
                const updated = {
                  ...jsonOrderingProviderNotFoundData,
                  fax_number: cleaned,
                };
                updateChartField({
                  jsonOrderingProviderNotFoundData: JSON.stringify(updated),
                });
              }
            }}
            slotProps={{
              htmlInput: {
                maxLength: 14,
              },

              inputLabel: { shrink: true },
            }}
          />
        </MUIGrid>
      </MUIGrid>
    );
  };

  /**
   * Renders the diagnosis codes section of the chart.
   * @returns {JSX.Element} Grid layout with diagnosis codes and infectious disease information display
   */
  const renderDiagnosisCodesSection = () => {
    const diagnosisCodeKeys = getDiagnosisCodeKeys(
      chartContext.chart[target][targetId],
    );
    return (
      <MUIGrid container spacing={2}>
        <MUIGrid size={12}>
          <MUIStack direction="row" spacing={1}>
            <MUIContentPasteIcon
              fontSize="medium"
              sx={{ color: (theme) => theme.palette.secondary.main }}
            />
            <MUITypography variant="subtitle2">Diagnosis</MUITypography>
          </MUIStack>
        </MUIGrid>

        {diagnosisCodeKeys.map((key, index) => {
          return (
            <MUIGrid
              key={key}
              sx={getContainerQuery(600)}
              size={{
                lg: 12,
                md: 12,
                xs: 12,
              }}
            >
              <MUIAutocomplete
                options={
                  diagnosisCodesFound.length === originalDiagnosisCodes.length
                    ? []
                    : diagnosisCodesFound
                }
                value={
                  diagnosisCodesFound.find(
                    (option) =>
                      option?.id ===
                      chartContext.chart[target][targetId]?.[key],
                  ) || null
                }
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }
                getOptionKey={(option) => option?.id || ""}
                getOptionLabel={(option) =>
                  DiagnosisCode.getDetailedDisplayName(option) || ""
                }
                inputValue={diagnosisCodesInputs[key] || ""}
                onChange={(e, newValue) => {
                  const updatedValue = newValue?.id || null;
                  const updates = { [key]: updatedValue };

                  // If target is orderActivity, copy all diagnosis codes to patient
                  if (target === "orderActivity") {
                    const diagnosisCodeKeys = getDiagnosisCodeKeys(
                      chartContext.chart[target][targetId],
                    );
                    const patientUpdates = {};

                    // Copy all diagnosis codes from orderActivity to patient, including the one being updated
                    diagnosisCodeKeys.forEach((diagKey) => {
                      const codeId =
                        diagKey === key
                          ? updatedValue
                          : chartContext.chart[target][targetId]?.[diagKey];
                      patientUpdates[diagKey] = codeId || null;
                    });

                    // Update both orderActivity and patient
                    chartContext.onChange({
                      [target]: { [targetId]: updates },
                      patient: { [chartContext.patientId]: patientUpdates },
                    });
                    setCardChart({ ...cardChart, ...updates });
                  } else {
                    updateChartField(updates);
                  }

                  setDiagnosisCodesInputs((inputs) => ({
                    ...inputs,
                    [key]:
                      newValue && DiagnosisCode.getDetailedDisplayName(newValue)
                        ? DiagnosisCode.getDetailedDisplayName(newValue)
                        : "",
                  }));
                }}
                onInputChange={(e, newInputValue, reason) => {
                  if (reason === "input") {
                    updateChartField({ [key]: null });
                    setDiagnosisCodesInputs((inputs) => ({
                      ...inputs,
                      [key]: newInputValue,
                    }));
                    throttledHandleDiagnosisCodeSearch(newInputValue);
                  }
                }}
                onBlur={() => {
                  setDiagnosisCodesInputs((inputs) => ({
                    ...inputs,
                    [key]: (() => {
                      const option = diagnosisCodesFound.find(
                        (option) =>
                          option?.id ===
                          chartContext.chart[target][targetId]?.[key],
                      );
                      return option
                        ? DiagnosisCode.getDetailedDisplayName(option)
                        : "";
                    })(),
                  }));
                }}
                noOptionsText="Type to search..."
                renderInput={(params) => (
                  <MUITextField
                    {...params}
                    size="small"
                    variant="standard"
                    label={
                      index === 0 ? "Primary Diagnosis" : `Additional Diagnosis`
                    }
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </MUIGrid>
          );
        })}
        <MUIGrid sx={getContainerQuery(600)} size={12}>
          <MUIFormControlLabel
            control={
              <MUICheckbox
                size="small"
                checked={patient?.hasInfectiousDisease}
                onChange={(event, value) => {
                  if (value) {
                    chartContext.onChange({
                      patient: {
                        [chartContext.patientId]: {
                          hasInfectiousDisease: value,
                        },
                      },
                    });
                    setCardChart({ ...cardChart, hasInfectiousDisease: value });
                  } else {
                    chartContext.onChange({
                      patient: {
                        [chartContext.patientId]: {
                          hasInfectiousDisease: value,
                          diseaseNotes: "",
                        },
                      },
                    });
                    setCardChart({
                      ...cardChart,
                      hasInfectiousDisease: value,
                      diseaseNotes: "",
                    });
                  }
                }}
              />
            }
            label="Infectious Disease"
          />
        </MUIGrid>
        {patient?.hasInfectiousDisease ? (
          <MUIGrid sx={getContainerQuery(600)} size={12}>
            <MUITextField
              value={
                patient && patient.diseaseNotes ? patient.diseaseNotes : ""
              }
              size="small"
              label="Disease Notes"
              variant="standard"
              fullWidth={true}
              disabled={!patient?.hasInfectiousDisease}
              onBlur={(e) =>
                chartContext.onChange({
                  patient: {
                    [chartContext.patientId]: {
                      diseaseNotes: e.target.value.trim(),
                    },
                  },
                })
              }
              onChange={(e) => {
                chartContext.onChange({
                  patient: {
                    [chartContext.patientId]: { diseaseNotes: e.target.value },
                  },
                });
                setCardChart({ ...cardChart, diseaseNotes: e.target.value });
              }}
              slotProps={{
                htmlInput: {
                  maxLength: 255,
                },

                inputLabel: { shrink: true },
              }}
            />
          </MUIGrid>
        ) : null}
      </MUIGrid>
    );
  };

  /**
   * Renders the edit view with form fields for editing clinical information.
   * @returns {JSX.Element} Grid layout with editable form fields
   */
  const renderEditView = () => {
    return (
      <MUIGrid container spacing={2} sx={{ containerType: "inline-size" }}>
        <MUIGrid
          container
          rowSpacing={3}
          sx={getContainerQuery(600)}
          size={{
            xs: 12,
            md: 6,
            lg: 6,
          }}
        >
          <MUIGrid size={12}>{renderOrderingProviderSection()}</MUIGrid>
          <MUIGrid size={12}>{renderProviderNotFoundSection()}</MUIGrid>
        </MUIGrid>
        {/* Right column: diagnosis codes and infectious disease */}
        <MUIGrid
          container
          sx={getContainerQuery(600)}
          size={{
            xs: 12,
            md: 6,
            lg: 6,
          }}
        >
          <MUIGrid size={12}>{renderDiagnosisCodesSection()}</MUIGrid>
        </MUIGrid>
      </MUIGrid>
    );
  };

  /**
   * Displays the provider information.
   * @returns {JSX.Element|string} JSX element or string displaying provider information
   */
  const displayProvider = () => {
    if (providersData && selectedProvider && !providerNotFound) {
      return (
        <>
          {Provider.getDisplayName(selectedProvider)}
          {selectedProvider?.npi && (
            <>
              <br />
              NPI: {selectedProvider.npi}
            </>
          )}
          {selectedProvider?.faxNumber && (
            <>
              <br />
              Fax: {Format.phone(selectedProvider.faxNumber)}
            </>
          )}
          {selectedProvider?.phoneNumber && (
            <>
              <br />
              Phone: {Format.phone(selectedProvider.phoneNumber)}
            </>
          )}
        </>
      );
    } else if (
      chartContext.chart[target][targetId].orderingProviderNotFound &&
      jsonOrderingProviderNotFoundData?.name !== ""
    ) {
      return (
        <>
          {jsonOrderingProviderNotFoundData.name}
          {jsonOrderingProviderNotFoundData.npi && (
            <>
              <br />
              NPI: {jsonOrderingProviderNotFoundData.npi}
            </>
          )}
          {jsonOrderingProviderNotFoundData.fax_number && (
            <>
              <br />
              Fax: {Format.phone(jsonOrderingProviderNotFoundData.fax_number)}
            </>
          )}
          {jsonOrderingProviderNotFoundData.phone_number && (
            <>
              <br />
              Phone:{" "}
              {Format.phone(jsonOrderingProviderNotFoundData.phone_number)}
            </>
          )}
        </>
      );
    } else {
      return "None";
    }
  };

  /**
   * Renders the read-only view displaying clinical information about the patient and order activity.
   * @returns {JSX.Element} Grid layout with clinical information display
   */
  const renderReadOnlyView = () => {
    const diagnosisCodes = getReadonlyDiagnosisCodes();
    const providerInfo = displayProvider();
    return (
      <MUIGrid container spacing={4} rowSpacing={2}>
        <MUIGrid sx={{ display: "flex" }} size="auto">
          <InfoItem
            icon={{
              icon: "Person",
              color: (theme) => theme.palette.secondary.main,
            }}
            label={"Ordering Provider"}
            value={providerInfo}
          />
        </MUIGrid>

        <MUIGrid size="auto">
          <InfoItem
            icon={{
              icon: "ContentPaste",
              color: (theme) => theme.palette.secondary.main,
            }}
            label={"Diagnosis Codes"}
            value={diagnosisCodes}
          />
        </MUIGrid>
        {patient && patient?.hasInfectiousDisease ? (
          <MUIGrid sx={{ display: "flex" }} size="auto">
            <InfoItem
              icon={{
                icon: "Coronavirus",
                color: (theme) => theme.palette.secondary.main,
              }}
              label={"Infectious Disease"}
              value={patient?.diseaseNotes || "No Details"}
            />
          </MUIGrid>
        ) : null}
      </MUIGrid>
    );
  };

  /** Resets form values to their original state.
   */
  const resetValues = () => {
    setProviderInput(
      (provider && Provider.getDetailedDisplayName(provider)) || "",
    );
    setDiagnosisCodesFound(
      Array.from(Object.values(chartContext.originalChart.diagnosisCode || [])),
    );
    const originalOrderingProvider =
      chartContext.originalChart.provider[
        chartContext.originalChart[target][targetId]?.orderingProviderId
      ];
    setProvidersData(
      originalOrderingProvider ? [originalOrderingProvider] : [],
    );
    const diagnosisCodeKeys = getDiagnosisCodeKeys(
      chartContext.chart[target][targetId],
    );
    const diagnosisCodesInputs = Object.fromEntries(
      diagnosisCodeKeys.map((key, idx) => [
        key,
        originalDiagnosisCodes[idx]
          ? DiagnosisCode.getDetailedDisplayName(originalDiagnosisCodes[idx]) ||
            ""
          : "",
      ]),
    );
    setDiagnosisCodesInputs(diagnosisCodesInputs);
    setCopyToPatient(false);
  };

  /**
   * Clears validation issues related to clinical fields in the chart context.
   */
  const clearValidationIssues = () => {
    const clinicalFields = cardChart ? Object.keys(cardChart) : [];
    const clearedIssues = { ...chartContext.validationIssues };

    delete clearedIssues[className];
    chartContext.updateValidationIssues(clearedIssues);
  };
  /**
   * Determines which view to render based on edit mode state.
   * @returns {JSX.Element} Either edit view or read-only view
   */
  const renderContent = () => {
    if (editMode) {
      return renderEditView();
    }
    return renderReadOnlyView();
  };

  /**
   * Returns the action buttons for the card component.
   * @returns {Array} Array of action button components
   */
  const getActions = () => {
    const handleCancel = () => {
      const updates = { ...chartContext.chart[target][targetId] };
      const originalOrderingProviderId =
        chartContext.originalChart[target][targetId]?.orderingProviderId;
      const originalOrderingProvider =
        chartContext.originalChart.provider?.[originalOrderingProviderId];

      const clinicalFields = cardChart ? Object.keys(cardChart) : [];

      clinicalFields.forEach((field) => {
        if (
          chartContext.chart[target][targetId][field] !==
          chartContext.originalChart[target][targetId][field]
        ) {
          updates[field] = chartContext.originalChart[target][targetId][field];
        }
      });

      // If target is orderActivity, also revert diagnosis codes on patient
      if (target === "orderActivity") {
        const diagnosisCodeKeys = getDiagnosisCodeKeys(
          chartContext.chart[target][targetId],
        );
        const patientUpdates = {};

        // Revert all diagnosis codes on patient to their original values
        diagnosisCodeKeys.forEach((diagKey) => {
          const originalCodeId =
            chartContext.originalChart[target][targetId]?.[diagKey];
          if (originalCodeId) {
            patientUpdates[diagKey] = originalCodeId;
          } else {
            patientUpdates[diagKey] = null;
          }
        });

        // Update both orderActivity and patient
        chartContext.onChange({
          [target]: {
            [targetId]: { ...chartContext.chart[target][targetId], ...updates },
          },
          patient: { [chartContext.patientId]: patientUpdates },
        });
      } else {
        chartContext.onChange({
          [target]: {
            [targetId]: { ...chartContext.chart[target][targetId], ...updates },
          },
        });
      }

      rollbackProviderContactEdits();
      if (originalOrderingProvider?.id) {
        chartContext.onChange({
          provider: { [originalOrderingProvider.id]: originalOrderingProvider },
        });
      }

      clearValidationIssues();
      resetValues();
      chartContext.setCardEditMode?.(className, false);
      setEditMode(false);
    };

    return [
      ...(editMode
        ? [
            <MUIButton
              color="error"
              disabled={chartContext.isUpdating}
              onClick={handleCancel}
              size="small"
            >
              Cancel
            </MUIButton>,
          ]
        : [
            <MUIButton
              disabled={chartContext.isUpdating}
              onClick={() => setEditMode(true)}
              size="small"
            >
              Edit
            </MUIButton>,
          ]),
    ];
  };

  return (
    <Card
      renderContent={renderContent}
      getActions={getActions}
      getContainerType={() => "inline-block"}
      getTitle={() => title}
      getIcon={() => icon}
    />
  );
}

/**
 * Helper: Get diagnosis code keys from a record.
 */
function getDiagnosisCodeKeys(record) {
  return Object.keys(record).filter((key) => /DiagnosisCodeId$/i.test(key));
}

CardChartClinical.meta = { className: className, title: title, icon: icon };
